import clx from "classnames";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useCallService } from "../../contexts/call-service-context";
import { useSnackbar } from "../../contexts/snackbar-provider";
import { Staff } from "../../types/staff";
import promptMicPermissions from "../../utils/prompt-mic-permissions";
import CallCustomerDetails from "./CallCustomerDetails";
import useCallReceiverStyles from "./KommunoCallReceiverStyles";
import { useAmeyoEvents } from "./use-ameyo-events";

interface AmeyoCallReceiverProps {
  user: Staff;
}

function AmeyoCallReceiver(props: AmeyoCallReceiverProps) {
  const 
     { user } = props,
    // { id } = user,
    clxs = useCallReceiverStyles(),
    {outgoingAmeyoCallUpdate} = useCallService(),
    ccx = clx("material-icons", clxs.fabIcon),
    [expanded, setExpanded] = useState<boolean>(false),
    [showCallDialer, setShowCallDialer] = useState<boolean>(true),
    [openCustomerDetail, setOpenCustomerDetails] = useState<boolean>(false),
    [outgoingCallDetail, setOutGoingCallDetail] = useState<OutgoingCallDetail>({
      number: "",
      vertical: "",
      opportunitySlug: "",
    }),
    iframeUrl = useMemo(() => {
      const crmOrigin = process.env.REACT_APP_AMEYO_CRM_ORIGIN || "https://oi-ubt.lohono.com";
      const ameyoBaseUrl = "https://app10.ameyoemerge.in:7443/";
      return (
        ameyoBaseUrl +
        "ameyowebaccess/toolbarLogin-vl.htm?" +
        "origin=" +
        encodeURIComponent(crmOrigin)
      );
    }, []),
    handleExpandedToggle = useCallback(
      () => setExpanded((expanded) => !expanded),
      [setExpanded],
    ),
    // loginRole = sessionStorage.getItem("loginRole"),
    enqueueSnackbar = useSnackbar(),
    [customerCallNumber, setCustomerCallNumber] = useState<string>(""),
    [callEventsData, setCallEventsData] =
      useState<CallEvents>(DEFAULT_EVENTS_DATA),
    // Add this ref at the top of your component to keep track of the timer
    callStatusTimeout = useRef<NodeJS.Timeout | null>(null),
    // Ref for outgoing call detail: Ameyo show_crm can fire before React commits setState,
    // so we store detail here synchronously to avoid race (missing crtId → Trip mapping).
    outgoingCallDetailRef = useRef<OutgoingCallDetail>({
      number: "",
      vertical: "",
      opportunitySlug: "",
    }),
    /** Trace ID for the current LMS-initiated outgoing call. */
    outgoingCallTraceIdRef = useRef<string>(""),
    /** Timeout handle for 90s "show_crm never received" warning; cleared when handleOutgoingCallUpdate runs. */
    showCrmTimeoutRef = useRef<NodeJS.Timeout | null>(null),
    [agentStatus, setAgentStatus] = useState<AgentStatus>(DEFAULT_AGENT_STATUS);

  const initiateMicCallPermissions = async () => {
    try {
      const mediaStream = await promptMicPermissions();

      if (!mediaStream) {
        enqueueSnackbar("mic permission not provided", "error");

        return;
      }
    } catch (e: any) {
      enqueueSnackbar(e.message, "error");

      return;
    }
  };

  const handleAmeyoIframeError = (e: any) => {
    console.error("Ameyo iframe failed to load", e?.nativeEvent || e);
  };

  // Use this function to set call events data and auto-clear after 3 seconds
  const showCallStatus = (data: CallEvents, duration = 6000) => {
    setCallEventsData(data);
    if (callStatusTimeout.current) {
      clearTimeout(callStatusTimeout.current);
    }
    callStatusTimeout.current = setTimeout(() => {
      setCallEventsData(DEFAULT_EVENTS_DATA); // Reset to default (hide status)
    }, duration);
  };

  // Outgoing call flow: CallDialer dispatches outgoing_call_initiated → we set detail and
  // call Ameyo doDial; Ameyo later sends show_crm with crtId. We must send crtId + trip
  // context to backend. Ameyo can fire show_crm before React commits setState (notably when
  // call is from Trip Details modal, which closes on onDone). We store detail in a ref so
  // handleOutgoingCallUpdate always has the correct vertical/opportunitySlug.
  const handleOutgoingCallInitiated = (event: CustomEvent) => {
    const { detail } = event;

    if (detail) {
      outgoingCallTraceIdRef.current = detail?.number ? `${detail.number}-${Date.now()}` : "";
      setExpanded(true);

      // Store in ref immediately so handleShowCrm (Ameyo) can read it before React commits state.
      outgoingCallDetailRef.current = {
        number: detail.number ?? "",
        vertical: detail.vertical ?? "",
        opportunitySlug: detail.opportunitySlug ?? "",
      };
      setOutGoingCallDetail(detail);

      const { number } = detail;
      const numberWoCountryCode = number.split(" ")[1];
      initiateOutgoingCall(numberWoCountryCode);
    }
  };

  // Initiate outgoing call at Ameyo.
  const initiateOutgoingCall = (number: string) => {
    if (!ameyo?.integration) {
      return;
    }

    // If show_crm is not received within 90s, log a warning so we can debug "API never triggered".
    if (showCrmTimeoutRef.current) {
      clearTimeout(showCrmTimeoutRef.current);
    }
    showCrmTimeoutRef.current = setTimeout(() => {
      showCrmTimeoutRef.current = null;
      console.warn("Outgoing call initiated but show_crm never received", { number });
    }, 90000);

    ameyo.integration.doDial(number, (response: any) => {
      console.log("Call initiated", response);
    });
  };

  // Listening to show CRM event. Called when call is received or when placing an outgoing call for call updates at Ameyo.
  const handleShowCrm = (phone: string, additionalParams: any) => {
    setExpanded(true);

    if (phone && phone.length) {
      setCustomerCallNumber(phone);
      setOpenCustomerDetails(true);
    }

    let additionalParamsJson: { crtObjectId?: string; callType?: string };
    try {
      additionalParamsJson =
        typeof additionalParams === "string"
          ? JSON.parse(additionalParams)
          : additionalParams ?? {};
    } catch (e) {
      console.error("[Ameyo] show_crm: failed to parse additionalParams", e);
      return;
    }

    const { crtObjectId } = additionalParamsJson;
    const callType = additionalParamsJson.callType ?? "";
    const isOutgoingCall = callType.includes("outbound");

    // Updating the backend of the call initiated. Must run when we have crtId so backend can map recording to trip.
    if (isOutgoingCall && crtObjectId) {
      handleOutgoingCallUpdate(crtObjectId);
    }

    if (additionalParamsJson.callType?.length) {
      if (isOutgoingCall) {
        showCallStatus(
          { call_direction: "OUTGOING", event_type: "IN PROGRESS" },
          3600000,
        );
      } else {
        showCallStatus(
          { call_direction: "INCOMING", event_type: "IN PROGRESS" },
          3600000,
        );
      }
    }
  };

  const handleOutgoingCallUpdate = async (crtId: string) => {
    if (showCrmTimeoutRef.current) {
      clearTimeout(showCrmTimeoutRef.current);
      showCrmTimeoutRef.current = null;
    }

    // Prefer ref (set synchronously in handleOutgoingCallInitiated) over state to avoid
    // race where Ameyo show_crm fires before React has committed setState.
    const refDetail = outgoingCallDetailRef.current;
    const detail =
      refDetail.vertical || refDetail.opportunitySlug
        ? refDetail
        : outgoingCallDetail;
    const { vertical, opportunitySlug } = detail;

    const { response, error } = await outgoingAmeyoCallUpdate(
      vertical || undefined,
      opportunitySlug || undefined,
      crtId,
    );

    if (error) {
      const message = error?.message || "Something went wrong.";
      enqueueSnackbar(message, "error");
    }

    if (response && !error) {
      enqueueSnackbar("Success.", "success");
      outgoingCallTraceIdRef.current = "";
      outgoingCallDetailRef.current = {
        number: "",
        vertical: "",
        opportunitySlug: "",
      };
    }
  };

  // Set user name and password and initiate login in Ameyo Iframe.Authorization policy to the user 'auth.type.crm.http','auth.type.password'
  const initiateAmeyoLogin = () => {
    const {email} = user;
    setTimeout(() => {
      // If user has logged out then only initiate the login.
      if(!agentStatus.agent_status.length || agentStatus.agent_status === "LOGGED_OUT") {
        // ameyo?.integration?.doLogin(email, "1234");
        ameyo?.integration?.setLoginInfo(email,"");
      }
    }, 1000);
  }

  // When user logs out from the OI we initiate ameyo logout as well.
  const initiateAmeyoLogout = () => {
    ameyo?.integration?.doLogout();
  }

  useEffect(() => {
    console.log("ameyo", ameyo);
    setExpanded(false);
    initiateAmeyoLogin();
  },[]);

  //Initialize mic call permissions and subscribe to channel.
  useEffect(() => {
    initiateMicCallPermissions();
    setShowCallDialer(true);
  }, []);

  //Listen to outgoing call initiated method.
  useEffect(() => {
    window.addEventListener(
      "outgoing_call_initiated",
      handleOutgoingCallInitiated as EventListener,
    );

    return () => {
      window.removeEventListener(
        "outgoing_call_initiated",
        handleOutgoingCallInitiated as EventListener,
      );
    };
  }, []);

  // Hide the component on logout and logout from the Ameyo.
  useEffect(() => {
    const handleStorageChange = () => {
      const newLoginRole = sessionStorage.getItem("loginRole");
      if (newLoginRole) {
        setShowCallDialer(true);
      } else {
        setShowCallDialer(false);
      }
    };

    const handleLogout = () => {
      handleStorageChange();
      initiateAmeyoLogout();
    }

    window.addEventListener("login", handleStorageChange);
    window.addEventListener("logout", handleLogout);

    return () => {
      window.removeEventListener("login", handleStorageChange);
      window.removeEventListener("logout", handleLogout);
    };
  }, []);

  // Listening to event status changes from Ameyo to perform actions on our CRM.
  const updateAgentStatus = (status: string) => {
    switch (status.toLowerCase()) {
      case "loggedin":
        setAgentStatus({agent_status: "LOGGED_IN", agent_icon: "account_circle"});
        break;
      case "loggedout":
        setAgentStatus({agent_status: "LOGGED_OUT", agent_icon: "lock_person"});
        break;
      case "ready":
        setAgentStatus({agent_status: "AVAILABLE", agent_icon: "headset_mic"});
        break;
      case "break":
        setAgentStatus({agent_status: "BREAK", agent_icon: "free_breakfast"});
        break;
      default:
        break;
    }
  }

  const handleAmeyoLogin = (response: any) => {
    console.log("Login successful", response)
  }

  const handleAmeyoLogout = (response: any) => {
    console.log("Logout successful", response)
    if(response == "USER_LOGOUT" || response == "success") {
      updateAgentStatus("loggedout")
    }
  }

  const handleAmeyoLoginStatus = (response: any) => {
    console.log("Login status", response)
    updateAgentStatus(response);
  }

  const handleAmeyoReady = (response: any) => {
    console.log("Ready", response)
    if(response?.toLowerCase().includes("available" ) || response?.toLowerCase().includes("ready")) {
      updateAgentStatus("Ready")
    }
  }

  const handleAmeyoBreak = (response: any) => {
    console.log("Break", response)
    updateAgentStatus("break");
  }

  const handleAmeyoHangup = (response: any) => {
    console.log("Hangup", response);
    if(response?.toLowerCase() == "success") {
      showCallStatus({call_direction: "", event_type: "HANGUP"})
    }
  }

  //Initialize Ameyo events.
  useAmeyoEvents({
    loginHandler: handleAmeyoLogin,
    loginStatusHandler: handleAmeyoLoginStatus,
    showCrm: handleShowCrm,
    readyHandler: handleAmeyoReady,
    breakHandler: handleAmeyoBreak,
    hangupHandler: handleAmeyoHangup,
    logoutHandler: handleAmeyoLogout,

    //doLogin: handleDoLogin,
    //doDial: handleDoDial,
    //doLogout: handleDoLogout,
  });

  return (
    <>
      {showCallDialer ? (
        <>
          <div
            className={clxs.callDialer}
            onClick={handleExpandedToggle}
          >
            <span className={ccx}>{expanded ? "expand_more" : "expand_less"}</span>

            {/* Agent status */}
            <span className={clxs.agentStatus}>
              <span className={ccx}>{agentStatus?.agent_icon}</span>
              {agentStatus?.agent_status.split("_").join(" ")}
            </span>

            {/* Call Status */}
            <span className={clxs.callType}>
              <span className={ccx}>
                {callEventsData?.call_direction == "INCOMING" && "call_received"}
                {callEventsData?.call_direction == "OUTGOING" && "call_made"}
                {/* {callEventsData?.call_direction == "MISSED" && "call_missed"} */}
              </span>
              <span className={clxs.eventStatus}>
                {callEventsData?.event_type && callEventsData.event_type}
              </span>
            </span>
          </div>
          <div
            className={clxs.floatingPanel}
            data-open={expanded}
          >
            <iframe
              allow="microphone; speaker; autoplay;"  
              height="500px"
              id="ameyoIframe"
              onError={(e) => handleAmeyoIframeError(e)}
              src={iframeUrl}
              style={{ border: "none" }}
              width="100%"
            />
          </div>
          <CallCustomerDetails
            callingNumber={customerCallNumber}
            onCloseCustomerDetails={() => setOpenCustomerDetails(false)}
            showCustomerDetails={openCustomerDetail}
          ></CallCustomerDetails>
        </>
      ) : (
        <></>
      )}
    </>
  );
}

export default AmeyoCallReceiver;

interface OutgoingCallDetail {
  number: string;
  vertical: string;
  opportunitySlug: string;
}

interface CallEvents {
  call_direction: "INCOMING" | "OUTGOING" | "MISSED" | "";
  event_type: "IN PROGRESS" | "HANGUP" | "";
}

interface AgentStatus {
  agent_status: "LOGGED_IN" | "LOGGED_OUT" | "AVAILABLE" | "BREAK" | "";
  agent_icon?: "lock_person" | "account_circle" | "headset_mic" | "free_breakfast" | ""
}

const DEFAULT_AGENT_STATUS: AgentStatus = {
  agent_status: "",
  agent_icon: "",
};

const DEFAULT_EVENTS_DATA: CallEvents = {
  call_direction: "",
  event_type: "",
};


// Frontend additonal params on show crm event
// {
//   "crm_push_generated_time": "1747225414568",
//   "userCrtObjectId": "agent@isprava-58ed19da-64fd-4a5c-a15c-fa53a58d5b91-uce-webrtc_agent_isprava@-78",
//   "phone": "7020293170",
//   "disableDispositionOnActiveCall": "false",
//   "campaignId": "3",
//   "associationType": "manualdial.association",
//   "crtObjectId": "d767-68223414-vce-15215",
//   "sessionId": "d767-68223414-ses-agent@isprava-qSEooNrGrhTNJz0mouMu214Y6Ca1VCwlXFJs7DeB1dZzrutKCWDs0sY1Dd9W0I5hHj9zNOmHxpYaW9ajyGF3JoiyyYYnhiotUksXX3y2wqc58lkyz3Zy-412",
//   "userId": "agent@isprava",
//   "isOrignalPhoneRequiredInCRM": "true",
//   "callType": "outbound.manual.dial",
//   "displayPhone": "7020293170"
// }


//Backend Post Data Parameters
// {
//   "callId": "d767-68223414-vcall-12565",
//   "queueId": null,
//   "customerCRTId": "d767-68223414-vce-11869",
//   "talkTime": "7680",
//   "callEndTime": "2025/05/14 14:13:08 +0530",
//   "campaignId": "3",
//   "userId": "agent@isprava",
//   "dispositionCode": "wrap.timeout",
//   "callType": "outbound.manual.dial",
//   "phone": "7020293170",
//   "dispositionClass": "campaign.system.disposition",
//   "recUrl": "https://app10.ameyoemerge.in:7443/ameyowebaccess/command?command=downloadVoiceLog\u0026data=%7B%22crtObjectId%22%3A%22d767-68223414-vce-11869%22%2C%22targetFormat%22%3A%22mp3%22%7D",
//   "callResult": "SUCCESS",
//   "callConnectedTime": "2025/05/14 14:13:01 +0530"
// }

